import React from 'react';
import { PlusIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui';

interface EmailRulesListHeaderProps {
  totalRules: number;
  enabledCount: number;
  disabledCount: number;
  onAdd: () => void;
}

const EmailRulesListHeader: React.FC<EmailRulesListHeaderProps> = ({
  totalRules,
  enabledCount,
  disabledCount,
  onAdd,
}) => {
  const { colors } = useTheme();

  return (
    <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
      <div className="flex flex-col gap-4 border-b px-4 py-4 sm:px-5 sm:py-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>Email rules</h1>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
                colors.surfaceHover,
                colors.textSecondary,
              )}
            >
              {totalRules} total
            </span>
          </div>
          <p className={cn('max-w-2xl text-sm leading-relaxed', colors.textMuted)}>
            Automate actions on incoming mail after sync — move, tag, assign, or set workflow status by conditions.
          </p>
        </div>

        <Button type="button" onClick={onAdd} className="shrink-0 self-start">
          <PlusIcon className="h-5 w-5" />
          New rule
        </Button>
      </div>

      {totalRules > 0 && (
        <div className="flex flex-wrap gap-2 px-4 py-3 sm:px-5">
          {enabledCount > 0 && (
            <SummaryChip label="Enabled" count={enabledCount} tone="success" />
          )}
          {disabledCount > 0 && (
            <SummaryChip label="Disabled" count={disabledCount} tone="warning" />
          )}
        </div>
      )}
    </div>
  );
};

function SummaryChip({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'success' | 'warning';
}) {
  const toneClass =
    tone === 'success'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300';

  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium', toneClass)}>
      {label}
      <span className="rounded-md bg-black/5 px-1.5 py-0 text-[11px] tabular-nums dark:bg-white/10">{count}</span>
    </span>
  );
}

export default EmailRulesListHeader;
